import React from "react";
import { Users, Ticket, Clock, Handshake } from "lucide-react";
import { useLang } from "../i18n";
import { FICHE, TIER_LABEL, FAQ } from "../content";
import { MonoTag } from "./motifs";
import { Layers } from "lucide-react";

// FAQ accordion (shown on every project page + Infos)
export const FaqBlock = ({ accent = "var(--ms-accent)" }) => {
  const { t } = useLang();
  return (
    <div data-testid="faq-block">
      <MonoTag style={{ color: accent }}>{t(FAQ.title)}</MonoTag>
      <div className="mt-5 space-y-3">
        {FAQ.list.map((f, i) => (
          <details key={i} data-testid={`faq-item-${i}`}
            className="group rounded-md border border-white/10 px-6 py-4"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-heading font-semibold">
              {t(f.q)}
              <span className="font-display text-xl group-open:rotate-45 transition-transform duration-200" style={{ color: accent }}>+</span>
            </summary>
            <p className="font-body text-sm text-white/75 mt-3">{t(f.a)}</p>
          </details>
        ))}
      </div>
    </div>
  );
};

// Recap card grid: Âge / Prix / Durée / Tier + Partenaires (mandatory on every project page)
export const FicheInfo = ({ fiche, accent = "var(--ms-accent)" }) => {
  const { t } = useLang();
  const items = [
    { id: "age", icon: Users, label: FICHE.age, value: fiche.ages },
    { id: "price", icon: Ticket, label: FICHE.price, value: fiche.price || FICHE.onRequest },
    { id: "duration", icon: Clock, label: FICHE.duration, value: fiche.duration },
  ];
  if (fiche.tier) items.push({ id: "tier", icon: Layers, label: FICHE.tier, value: TIER_LABEL[fiche.tier] });
  return (
    <div data-testid="fiche-info">
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${items.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
        {items.map(({ id, icon: Icon, label, value }) => (
          <div key={id} data-testid={`fiche-${id}`}
            className="rounded-md p-6 border border-white/10"
            style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${typeof accent === "string" ? accent : "var(--ms-accent)"}` }}>
            <Icon size={22} style={{ color: accent }} />
            <MonoTag className="block text-white/50 mt-4">{t(label)}</MonoTag>
            <p className="font-heading text-lg font-semibold mt-2">{t(value)}</p>
          </div>
        ))}
      </div>
      <div data-testid="fiche-partners" className="mt-4 rounded-md p-6 border border-white/10"
        style={{ backgroundColor: "var(--ms-surface)" }}>
        <div className="flex items-center gap-3">
          <Handshake size={22} style={{ color: accent }} />
          <MonoTag className="text-white/50">{t(FICHE.partners)}</MonoTag>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          {fiche.partners && fiche.partners.length > 0 ? (
            fiche.partners.map((pt, i) => (
              <div key={i} className="flex items-center gap-2 bg-white rounded-md px-4 py-2">
                {pt.logo && <img src={pt.logo} alt={pt.name} className="h-8 w-auto object-contain" />}
                <span className="font-heading text-sm text-black">{pt.name}</span>
              </div>
            ))
          ) : (
            [0, 1, 2].map((i) => (
              <div key={i}
                className="w-28 h-14 rounded-md border-2 border-dashed border-white/20 flex items-center justify-center font-mono text-[8px] uppercase tracking-[0.15em] text-white/40 text-center px-2">
                {t(FICHE.partnerSoon)}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
